import { IRoute } from "@lib/types/Routes";
import { AuthRoles } from "@lib/types/Auth";
import { ROUTES } from "@lib/constants";

// shared routes
import MyProfile from "@app/MyProfile";
import WalletPoolList from "@app/shared/walletAddressListBilling";
import BlockchainPaymentHistory from "@app/shared/blockchainPayments";
import AuditLogsList from "@app/Monitoring/AuditLogs/List";
import AuditLogDetail from "@app/Monitoring/AuditLogs/Detail";
import Notifications from "@app/Notifications";
import NotesPage from "@app/Notes";

// customer routes
import * as CustomerDashboards from "@app/customerAgent";
import CustomerSettings from "@app/customerAgent/settings/CustomerSettings";
import * as CustomerPayments from "@app/customerAgent/payments";

// provider routes
import * as ProviderRoutes from "@app/provider";

// admin routes
import * as AdminDashboards from "@app/admin/dashboards";
import ProductsList from "@app/admin/products/List";
import TelegramMessages from "@app/admin/messages/Telegram";
import {
  PaymentProviderList,
  PaymentProviderDetail,
  PaymentProviderManagers,
  PaymentProviderManagerDetail,
  PaymentProviderPaymentHistories,
  PaymentProviderPaymentTransactionConfigs,
} from "@app/admin/paymentProvider";
import {
  CustomerWalletList,
  CustomerBlockchainPaymentList,
  CustomerManagers,
  CustomerInvoices,
  CustomerCashPaymentHistory,
  CustomerList,
  CustomerDetail,
  CustomerBlockchainPaymentRequestList,
  CustomerManagerDetail,
  CustomerPaymentHistories,
} from "@app/admin/customer";
import {
  CustomerLeadWalletList,
  CustomerLeadBlockchainPaymentList,
  CustomerLeadList,
  CustomerLeadDetail,
  CustomerLeadBlockchainPaymentRequestList,
} from "@app/admin/customerLead";
import {
  SharedCashPaymentHistory,
  SharedInvoices,
  SharedManagerDetail,
  SharedManagers,
  SharedPaymentHistory,
  SharedWalletAddressSendByteList,
  SharedWalletListSendByte,
  SharedBlockchainAIPaymentList,
  SharedBlockchainAIPaymentRequestList,
  SharedWalletAddressAIList,
  SharedAILogs,
  SharedTftSalesCallModels,
  SharedCustomerVoipConfigs,
} from "@app/shared";
import {
  AppRestrictionConfig,
  BillingConfigs,
  TelegramBotConfigs,
  TelegramBotConfigList,
  TelegramBotUsersList,
  TwoFactorAuthConfig,
} from "@app/admin/settings";
import TelegramBotConfigCanvas from "@app/admin/settings/TelegramBotConfig/Canvas";
import AdminManagementDashboard from "@app/adminManagement";
import TftSalesLeads from "@app/admin/tftSales/leads";
import TftSalesHistories from "@app/admin/tftSales/histories";
import {
  MarketPlaceEmailTemplateList,
  MarketPlaceSummaryList,
  MarketPlaceTradeAdviseList,
  MarketPlaceTraderAnalysisList,
  MarketPlaceWhatsappMessageList,
} from "@app/admin/marketPlace";
import MarketPlaceNoteValidationList from "@app/admin/marketPlace/noteValidation";
import MarketPlaceFraudDetectionList from "@app/admin/marketPlace/fraudDetection";

export const authorizedRoutes: IRoute[] = [
  //
  // #########################################################
  // ################### Customer Routes #####################
  // #########################################################
  //
  {
    path: [ROUTES.CUSTOMER.DASHBOARD],
    element: <CustomerDashboards.CustomerDashboard />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER.SETTINGS],
    element: <CustomerSettings />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER.INVOICE.LIST],
    element: <CustomerInvoices />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER.BLOCKCHAIN_PAYMENT_REQUEST.LIST],
    element: <CustomerPayments.CustomerBlockchainPaymentRequestList />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER.BLOCKCHAIN_PAYMENTS.LIST],
    element: <CustomerPayments.CustomerBlockchainPaymentList />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER.CASH_PAYMENT_HISTORY.LIST],
    element: <CustomerCashPaymentHistory />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER.PAYMENT_HISTORY.LIST],
    element: <CustomerPayments.CustomerPaymentHistories />,
    roles: [AuthRoles.CustomerAgent, AuthRoles.CustomerManager],
  },

  //
  // #########################################################
  // ################### Provider Routes #####################
  // #########################################################
  //
  {
    path: [ROUTES.PROVIDER.DASHBOARD],
    element: <ProviderRoutes.ProviderDashboard />,
    roles: [AuthRoles.ProviderAgent],
  },
  {
    path: [ROUTES.PROVIDER.PAYMENT_HISTORY.LIST],
    element: <ProviderRoutes.ProviderPaymentHistories />,
    roles: [AuthRoles.ProviderAgent],
  },
  {
    path: [ROUTES.PROVIDER.PAYMENT_TRANSACTION_CONFIG.LIST],
    element: <ProviderRoutes.ProviderPaymentTransactionConfigs />,
    roles: [AuthRoles.ProviderAgent],
  },

  //
  // #########################################################
  // ################### Admin Management Routes #####################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN_MANAGEMENT.DASHBOARD],
    element: <AdminManagementDashboard />,
    roles: [AuthRoles.Admin],
    isManagement: true,
  },

  //
  // #########################################################
  // ###################### Dashboards ######################
  // #########################################################
  //
  {
    path: ROUTES.ADMIN.DASHBOARD.CUSTOMER,
    element: <AdminDashboards.CustomerDashboard />,
    roles: [AuthRoles.Admin],
  },

  {
    path: ROUTES.ADMIN.DASHBOARD.CUSTOMER_LEAD,
    element: <AdminDashboards.CustomerLeadDashboard />,
    roles: [AuthRoles.Admin],
  },

  {
    path: ROUTES.ADMIN.DASHBOARD.PAYMENT_PROVIDER,
    element: <AdminDashboards.PaymentProviderDashboard />,
    roles: [AuthRoles.Admin],
  },

  {
    path: ROUTES.ADMIN.DASHBOARD.AI_PAYMENTS,
    element: <AdminDashboards.AIPaymentsDashboard />,
    roles: [AuthRoles.Admin],
  },

  {
    path: ROUTES.ADMIN.DASHBOARD.TFT_SALES,
    element: <AdminDashboards.TftSalesDashboard />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ###################### Wallet Pool ######################
  // #########################################################
  //
  {
    path: [
      ROUTES.WALLET_ACCOUNTS.LIST,
      ROUTES.WALLET_ACCOUNTS.WALLET_ID,
      ROUTES.WALLET_ACCOUNTS.LEAD,
      ROUTES.WALLET_ACCOUNTS.LEAD_WALLET_ID,
    ],
    element: <WalletPoolList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.SEND_BYTE_WALLETS.LIST],
    element: <SharedWalletListSendByte />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },

  {
    path: [
      ROUTES.SEND_BYTE_WALLET_ACCOUNTS.LIST,
      ROUTES.SEND_BYTE_WALLET_ACCOUNTS.WALLET_ID,
      ROUTES.SEND_BYTE_WALLET_ACCOUNTS.LEAD,
      ROUTES.SEND_BYTE_WALLET_ACCOUNTS.LEAD_WALLET_ID,
    ],
    element: <SharedWalletAddressSendByteList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [
      ROUTES.ADMIN.CUSTOMER.WALLET_ACCOUNTS.LIST,
      ROUTES.ADMIN.CUSTOMER.WALLET_ACCOUNTS.WALLET_ID,
      ROUTES.ADMIN.CUSTOMER.WALLET_ACCOUNTS.LEAD,
      ROUTES.ADMIN.CUSTOMER.WALLET_ACCOUNTS.LEAD_WALLET_ID,
    ],
    element: <CustomerWalletList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [
      ROUTES.ADMIN.CUSTOMER_LEAD.WALLET_ACCOUNTS.LIST,
      ROUTES.ADMIN.CUSTOMER_LEAD.WALLET_ACCOUNTS.WALLET_ID,
      ROUTES.ADMIN.CUSTOMER_LEAD.WALLET_ACCOUNTS.LEAD,
      ROUTES.ADMIN.CUSTOMER_LEAD.WALLET_ACCOUNTS.LEAD_WALLET_ID,
    ],
    element: <CustomerLeadWalletList />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ###################### AI Wallets #####################
  // #########################################################
  //
  {
    path: [
      ROUTES.AI_PAYMENTS.WALLET_LIST,
      ROUTES.AI_PAYMENTS.WALLET_ID,
      ROUTES.AI_PAYMENTS.CUSTOMER_ID,
      ROUTES.AI_PAYMENTS.CUSTOMER_WALLET_ID,
    ],
    element: <SharedWalletAddressAIList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.AI_PAYMENTS.BLOCKCHAIN_PAYMENT_REQUESTS],
    element: <SharedBlockchainAIPaymentRequestList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.AI_PAYMENTS.BLOCKCHAIN_PAYMENTS],
    element: <SharedBlockchainAIPaymentList />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ###################### AI Logs #####################
  // #########################################################
  //
  {
    path: [ROUTES.AI_LOGS.LIST],
    element: <SharedAILogs />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ####################### TFT SALES #######################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.TFT_SALES.LEAD_LIST],
    element: <TftSalesLeads />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.TFT_SALES.CALL_LIST],
    element: <TftSalesHistories />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.XAI_CALL_MODELS.LIST],
    element: <SharedTftSalesCallModels />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER_VOIP_CONFIG.LIST],
    element: <SharedCustomerVoipConfigs />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ###################### System Users #####################
  // #########################################################
  //
  {
    path: [ROUTES.MANAGER.LIST],
    element: <SharedManagers />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.MANAGER.LIST],
    element: <CustomerManagers />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.PAYMENT_PROVIDER.MANAGER.LIST],
    element: <PaymentProviderManagers />,
    roles: [AuthRoles.Admin],
  },

  {
    path: [ROUTES.MANAGER.DETAIL, ROUTES.MANAGER.DETAIL_TAB],
    element: <SharedManagerDetail />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [
      ROUTES.ADMIN.CUSTOMER.MANAGER.DETAIL,
      ROUTES.ADMIN.CUSTOMER.MANAGER.DETAIL_TAB,
    ],
    element: <CustomerManagerDetail />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [
      ROUTES.ADMIN.PAYMENT_PROVIDER.MANAGER.DETAIL,
      ROUTES.ADMIN.PAYMENT_PROVIDER.MANAGER.DETAIL_TAB,
    ],
    element: <PaymentProviderManagerDetail />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ###################### Invoices #####################
  // #########################################################
  //
  {
    path: [ROUTES.INVOICE.LIST],
    element: <SharedInvoices />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.INVOICE.LIST],
    element: <CustomerInvoices />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Blockchain Payment Requests #########
  // #########################################################
  //
  {
    path: [ROUTES.BLOCKCHAIN_PAYMENT_REQUEST.LIST],
    element: <CustomerBlockchainPaymentRequestList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.BLOCKCHAIN_PAYMENT_REQUEST.LIST],
    element: <CustomerBlockchainPaymentRequestList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.CUSTOMER_LEAD.BLOCKCHAIN_PAYMENT_REQUESTS.LIST],
    element: <CustomerLeadBlockchainPaymentRequestList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER_LEAD.BLOCKCHAIN_PAYMENT_REQUESTS.LIST],
    element: <CustomerLeadBlockchainPaymentRequestList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },

  //
  // #########################################################
  // ################### Blockchain Payments #################
  // #########################################################
  //
  {
    path: [ROUTES.BLOCKCHAIN_PAYMENTS.LIST, ROUTES.BLOCKCHAIN_PAYMENTS.TYPE],
    element: <BlockchainPaymentHistory />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [
      ROUTES.CUSTOMER_LEAD.BLOCKCHAIN_PAYMENTS.LIST,
      ROUTES.ADMIN.CUSTOMER_LEAD.BLOCKCHAIN_PAYMENTS.LIST,
    ],
    element: <CustomerLeadBlockchainPaymentList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.BLOCKCHAIN_PAYMENTS.LIST],
    element: <CustomerBlockchainPaymentList />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Payment History #####################
  // #########################################################
  //
  {
    path: [ROUTES.PAYMENT_HISTORY.LIST],
    element: <SharedPaymentHistory />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.PAYMENT_PROVIDER.PAYMENT_HISTORY.LIST],
    element: <PaymentProviderPaymentHistories />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.PAYMENT_HISTORY.LIST],
    element: <CustomerPaymentHistories />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Payment Transaction Configs #########
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.PAYMENT_PROVIDER.PAYMENT_TRANSACTION_CONFIG.LIST],
    element: <PaymentProviderPaymentTransactionConfigs />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Cash Payment History #################
  // #########################################################
  //
  {
    path: [ROUTES.CASH_PAYMENT_HISTORY.LIST],
    element: <SharedCashPaymentHistory />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.CASH_PAYMENT_HISTORY.LIST],
    element: <CustomerCashPaymentHistory />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Payment Providers ####################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.PAYMENT_PROVIDER.LIST],
    element: <PaymentProviderList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [
      ROUTES.ADMIN.PAYMENT_PROVIDER.DETAIL,
      ROUTES.ADMIN.PAYMENT_PROVIDER.DETAIL_TAB,
    ],
    element: <PaymentProviderDetail />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### White Label #########################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.CUSTOMER.LIST],
    element: <CustomerList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.CUSTOMER.DETAIL, ROUTES.ADMIN.CUSTOMER.DETAIL_TAB],
    element: <CustomerDetail />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### White Label Lead ####################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.CUSTOMER_LEAD.LIST, ROUTES.CUSTOMER_LEAD.LIST],
    element: <CustomerLeadList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },

  {
    path: [
      ROUTES.ADMIN.CUSTOMER_LEAD.DETAIL,
      ROUTES.ADMIN.CUSTOMER_LEAD.DETAIL_TAB,
      ROUTES.CUSTOMER_LEAD.DETAIL,
      ROUTES.CUSTOMER_LEAD.DETAIL_TAB,
    ],
    element: <CustomerLeadDetail />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },

  //
  // #########################################################
  // ################### Products #########################
  // #########################################################
  //
  {
    path: [ROUTES.PRODUCT.LIST],
    element: <ProductsList />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Market Place #########################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.EMAIL_TEMPLATE.LIST],
    element: <MarketPlaceEmailTemplateList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.EMAIL_TEMPLATE.DETAIL],
    element: <MarketPlaceEmailTemplateList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.SUMMARY.LIST],
    element: <MarketPlaceSummaryList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.SUMMARY.DETAIL],
    element: <MarketPlaceSummaryList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.WHATSAPP_MESSAGE.LIST],
    element: <MarketPlaceWhatsappMessageList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.WHATSAPP_MESSAGE.DETAIL],
    element: <MarketPlaceWhatsappMessageList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.TRADER_ANALYSIS.LIST],
    element: <MarketPlaceTraderAnalysisList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.TRADER_ANALYSIS.DETAIL],
    element: <MarketPlaceTraderAnalysisList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.TRADE_ADVISE.LIST],
    element: <MarketPlaceTradeAdviseList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.TRADE_ADVISE.DETAIL],
    element: <MarketPlaceTradeAdviseList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.NOTE_VALIDATION.LIST],
    element: <MarketPlaceNoteValidationList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.MARKET_PLACE.FRAUD_DETECTION.LIST],
    element: <MarketPlaceFraudDetectionList />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Monitoring #########################
  // #########################################################
  //
  {
    path: [ROUTES.MONITORING.AUDIT_LOGS.LIST],
    element: <AuditLogsList />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },

  {
    path: [ROUTES.MONITORING.AUDIT_LOGS.DETAIL],
    element: <AuditLogDetail />,
    roles: [AuthRoles.Admin, AuthRoles.CustomerManager],
  },

  //
  // #########################################################
  // ################### Notes #########################
  // #########################################################
  //
  {
    path: [ROUTES.NOTES.LIST],
    element: <NotesPage />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### Settings #########################
  // #########################################################
  //
  {
    path: [ROUTES.ADMIN.SETTINGS.APP_RESTRICTION_CONFIG.LIST],
    element: <AppRestrictionConfig />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.SETTINGS.TWO_FACTOR_AUTH_CONFIG.LIST],
    element: <TwoFactorAuthConfig />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.SETTINGS.BILLING_CONFIGS.LIST],
    element: <BillingConfigs />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_CONFIG.LIST],
    element: <TelegramBotConfigs />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [
      ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_CONFIG.DETAIL,
      ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_CONFIG.LIST_TAB,
    ],
    element: <TelegramBotConfigList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_CONFIG.CANVAS_TAB],
    element: <TelegramBotConfigCanvas />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [ROUTES.ADMIN.TELEGRAM_BOT.TELEGRAM_BOT_USERS.LIST],
    element: <TelegramBotUsersList />,
    roles: [AuthRoles.Admin],
  },
  {
    path: [
      "/messages/telegram",
      "/messages/telegram/:tabId",
      "/messages/telegram/:tabId/:chatId",
    ],
    element: <TelegramMessages />,
    roles: [AuthRoles.Admin],
  },

  //
  // #########################################################
  // ################### My Profile #########################
  // #########################################################
  //
  {
    path: [ROUTES.MY_PROFILE.LIST, ROUTES.MY_PROFILE.DETAIL_TAB],
    element: <MyProfile />,
  },

  //
  // #########################################################
  // ################### Notifications #####################
  // #########################################################
  //
  {
    path: [ROUTES.NOTIFICATIONS.LIST],
    element: <Notifications />,
  },
];
